import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { isFirebaseConfigured } from "@/lib/firebase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Eye, EyeOff } from "lucide-react";

const LOGO = "/smartfx-logo.png";

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function Login() {
  const { login, loginWithGoogle, playTransition, endTransition } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [remember, setRemember] = useState(true);

  useEffect(() => {
    api.get("/maintenance").then(({ data }) => setMaintenance(!!data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    const v = params.get("verified");
    if (v === "1") toast.success("Imèl ou verifye! Ou ka konekte kounye a.");
    else if (v === "0") toast.error("Lyen verifikasyon an pa valab oswa li fin ekspire.");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(identifier, password, remember);
      await playTransition("login");
      // Everyone lands in the normal app. Staff/mentors open the Admin panel from Settings.
      const dest = u?.role === "partner" ? "/partner" : "/app";
      navigate(dest);
      setTimeout(endTransition, 700);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const googleSubmit = async () => {
    setError("");
    setGoogleBusy(true);
    try {
      await loginWithGoogle();
      await playTransition("login");
      navigate("/app");
      setTimeout(endTransition, 700);
    } catch (err) {
      const code = err?.code || "";
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        setGoogleBusy(false);
        return;
      }
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setGoogleBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grain flex flex-col">
      <div className="p-6">
        <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-2 text-sm text-[#888] hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md fade-up">
          <div className="flex items-center gap-2 mb-8">
            <img src={LOGO} alt="Smart FX" className="h-11 w-auto object-contain" />
            <span className="font-heading text-xl font-bold tracking-tight">SMART FX</span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Welcome back</h1>
          <p className="text-[#888] text-sm mb-8">Sign in to your AutoTrade dashboard.</p>

          {maintenance && (
            <div data-testid="login-maintenance-notice" className="mb-6 rounded-md border border-[#ff2d2d]/50 bg-[#ff2d2d]/10 px-4 py-3 flex items-start gap-2.5">
              <span className="h-2 w-2 rounded-full bg-[#ff2d2d] mt-1.5 animate-pulse shrink-0" />
              <p className="text-xs text-[#ffb3b3] leading-relaxed">
                <strong className="text-white">Scheduled Maintenance.</strong> Client sign-in is temporarily unavailable. Only staff accounts can access the platform right now.
              </p>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="identifier" className="text-xs font-mono-term uppercase tracking-widest text-[#888]">Email or username</Label>
              <Input id="identifier" data-testid="login-email-input" type="text" required value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@email.com · @username"
                className="mt-2 bg-[#0F0F0F] border-[#222] rounded-sm h-11 focus:ring-white focus:border-white" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono-term uppercase tracking-widest text-[#888]">Password</Label>
              <div className="relative mt-2">
                <Input id="password" data-testid="login-password-input" type={showPass ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#0F0F0F] border-[#222] rounded-sm h-11 pr-11 focus:ring-white focus:border-white" />
                <button type="button" data-testid="toggle-login-password" onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888] hover:text-white transition-colors" aria-label={showPass ? "Hide password" : "Show password"}>
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p data-testid="login-error" className="text-sm text-[#FF3B30]">{error}</p>}
            <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="remember-me">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                data-testid="remember-me-checkbox"
                className="h-4 w-4 rounded-sm accent-[#00F593] cursor-pointer" />
              <span className={`text-sm transition-colors ${remember ? "text-[#00F593]" : "text-[#aaa]"}`}>Kenbe m konekte (Remember me)</span>
            </label>
            <Button type="submit" data-testid="login-submit-button" disabled={busy}
              className="w-full h-11 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold transition-colors">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          {isFirebaseConfigured() && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="h-px flex-1 bg-[#222]" />
                <span className="text-xs text-[#666] uppercase tracking-widest">or</span>
                <div className="h-px flex-1 bg-[#222]" />
              </div>
              <button type="button" data-testid="google-login-button" onClick={googleSubmit} disabled={googleBusy || busy}
                className="w-full h-11 rounded-sm bg-white text-black font-semibold flex items-center justify-center gap-2.5 hover:bg-[#e5e5e5] transition-colors disabled:opacity-60">
                {googleBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><GoogleG /> Continue with Google</>}
              </button>
            </>
          )}

          <p className="text-sm text-[#888] mt-6">
            No account?{" "}
            <Link to="/register" data-testid="go-register-link" className="text-white underline underline-offset-4">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
